import { FirebaseAuthentication, type User } from '@capacitor-firebase/authentication';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { getSelfAppData } from '../../../@api-client';
import { logCreateMutation } from '../../../@api-client/@tanstack/react-query.gen';
import { useForm } from '../../hooks/use-form';
import { useAuth } from '../../utils/auth/hooks/useAuth';
import { CheckUpdateResult, createCapacitorOtaUpdater } from '../../utils/ota-updater/ota-updater';
import { UserActivityTracker } from '../../utils/user/user-activity-tracker.service';
import { AuthLayout } from '../auth-layout/auth-layout';
import { AuthLayoutIduFooter } from '../auth-layout/components/auth-layout-idu-footer';
import { AuthErrorAlert } from './login-page/auth-error-alert/auth-error-alert';
import { OauthSection } from './login-page/oauth-section/oauth-section';
import { LoginCredentialsSection } from './login-page/login-credentials-section/login-credentials-section';
import { LoginColumn } from './login-page/login-column/login-column';
import { LoginIntro } from './login-page/login-intro/login-intro';
import {
  loginSchema,
  type LoginFormData,
  type LoginPageProps,
} from './login-page/login-page.types';
import { OtaInstructionContent } from './login-page/ota-instruction-content/ota-instruction-content';
import { SignupPrompt } from './login-page/signup-prompt/signup-prompt';
import { TwoStepSteps } from './login-page/two-step/two-step-steps';
import {
  useTwoStepSignIn,
  type TwoStepSignInState,
} from './login-page/two-step/use-two-step-sign-in';

export type { OtaLoginConfig } from './login-page/login-page.types';

// Google puts `amr` and `auth_time` in the ID token only when the sign-in asks for them, and the API
// needs both to pass two-step on a Google sign-in that used 2-Step Verification. Native Google sign-in
// ignores custom parameters.
const GOOGLE_SIGN_IN_STRENGTH_CLAIMS = JSON.stringify({
  id_token: { amr: { essential: true }, auth_time: { essential: true } },
});

const FIREBASE_ERROR_MESSAGES: Record<string, string> = {
  'auth/invalid-credential': 'Invalid email or password',
  'auth/user-not-found': 'No account found with this email',
  'auth/wrong-password': 'Invalid password',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/account-exists-with-different-credential':
    'This email already uses another sign-in method. Sign in with email and password first.',
  'auth/cancelled-popup-request': 'Google sign-in was cancelled.',
  'auth/network-request-failed': 'Network error while signing in with Google. Please try again.',
  'auth/operation-not-allowed':
    'Google sign-in is not enabled for this Firebase project. Contact your administrator.',
  'auth/popup-blocked':
    'Your browser blocked the Google sign-in popup. Allow popups and try again.',
  'auth/popup-closed-by-user': 'Google sign-in was cancelled.',
  'auth/unauthorized-domain':
    'This app origin is not authorized for Google sign-in. Contact your administrator.',
  'auth/admin-restricted-operation': 'Could not log in. Please contact your manager.',
};

function toFirebaseAuthError(err: { code?: string; message?: string }): string {
  const message = err.message ?? '';

  if (message.includes('Google must verify your email')) {
    return 'Google must verify your email before you can sign in.';
  }

  const code = err.code ?? '';
  const mappedMessage = FIREBASE_ERROR_MESSAGES[code];

  if (mappedMessage) {
    return mappedMessage;
  }

  if (code) {
    return `Failed to sign in (${code}). Please try again.`;
  }

  return 'Failed to sign in. Please try again.';
}

type LoginAuditMutate = (variables: {
  body: {
    type: 'audit';
    level: 'info';
    args: string[];
    timestamp: string;
    meta: {
      message: string;
      action: 'Log In';
      email: string;
      name: string;
    };
  }[];
}) => void;

function logSuccessfulLogin(user: User, mutate: LoginAuditMutate) {
  mutate({
    body: [
      {
        type: 'audit',
        level: 'info',
        args: [],
        timestamp: new Date().toISOString(),
        meta: {
          message: `[${user.email || 'UNKNOWN'}] Logged In`,
          action: 'Log In',
          email: user.email || 'UNKNOWN',
          name: user.displayName || 'UNKNOWN',
        },
      },
    ],
  });

  new UserActivityTracker().updateActivityToNow();
}

function usePostLoginRedirect({
  onError,
  requiredApplication,
  requiredApplicationName = 'this app',
  twoStep,
  takeGoogleIdToken,
}: Pick<LoginPageProps, 'requiredApplication' | 'requiredApplicationName'> & {
  onError: (error: string) => void;
  twoStep: TwoStepSignInState;
  takeGoogleIdToken: () => string | undefined;
}) {
  const navigate = useNavigate();
  const { logOut, accessToken, firebaseUser } = useAuth();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const twoStepRef = useRef(twoStep);
  twoStepRef.current = twoStep;
  const firebaseUserRef = useRef(firebaseUser);
  firebaseUserRef.current = firebaseUser;
  const takeGoogleIdTokenRef = useRef(takeGoogleIdToken);
  takeGoogleIdTokenRef.current = takeGoogleIdToken;

  useEffect(() => {
    if (!accessToken) return;

    let destination = '/';

    const refuse = async (message: string) => {
      onErrorRef.current(message);
      twoStepRef.current.reset();
      await logOut();
    };

    // The token is passed explicitly: the auth provider may not have it yet when this runs.
    const continueIntoApp = () =>
      getSelfAppData({ cache: 'no-cache', headers: { Authorization: `Bearer ${accessToken}` } })
        .then(async (result) => {
          if (result.error) {
            await refuse(result.error?.error?.message ?? 'Unable to sign in. Please try again.');
            return;
          }

          const hasRequiredApplicationAccess = requiredApplication
            ? result.data.data.venues.some((venue) => venue.application === requiredApplication)
            : true;

          if (!hasRequiredApplicationAccess) {
            await refuse(
              `You do not have access to ${requiredApplicationName}. Please contact your manager.`,
            );
            return;
          }

          if (returnTo) {
            destination = returnTo;
          }
          navigate(destination, { replace: true });
        })
        .catch(() => refuse('Unable to sign in. Please try again.'));

    twoStepRef.current
      .start({
        token: accessToken,
        uid: firebaseUserRef.current?.uid,
        email: firebaseUserRef.current?.email ?? undefined,
        googleIdToken: takeGoogleIdTokenRef.current(),
      })
      .then(
        (mayContinue) => (mayContinue ? continueIntoApp() : undefined),
        // Without a status, the self call either signs in an account without two-step or reports the gate's refusal.
        () => continueIntoApp(),
      );
  }, [accessToken, navigate, returnTo, logOut, requiredApplication, requiredApplicationName]);
}

export const LoginPage = ({
  logo: Logo,
  hideFooter,
  hideLogin = false,
  enableGoogleLogin = false,
  instructions,
  instructionBlock,
  hideSignup,
  requiredApplication,
  requiredApplicationName,
  ota,
}: LoginPageProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [isCheckingOta, setIsCheckingOta] = useState(Boolean(ota));
  const [otaEnforcement, setOtaEnforcement] = useState<CheckUpdateResult | null>(null);

  const loginMessage = localStorage.getItem('loginMessage');
  const [searchParams] = useSearchParams();
  const hasInactivityLogout = searchParams.get('reason') === 'inactivity';
  const isUpdateMandatory = Boolean(otaEnforcement?.isUpdateMandatory);

  const handleAppStoreClick = useCallback(() => {
    const appStoreUrl = ota?.appStoreUrl;
    if (!appStoreUrl) return;

    window.open(appStoreUrl, '_blank');
  }, [ota?.appStoreUrl]);

  useEffect(() => {
    if (!ota) return;

    const otaUpdater = createCapacitorOtaUpdater(ota.config);
    otaUpdater
      .checkForUpdate()
      .then(setOtaEnforcement)
      .finally(() => setIsCheckingOta(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { logOut } = useAuth();
  const twoStep = useTwoStepSignIn();
  const googleIdTokenRef = useRef<string | undefined>(undefined);
  const takeGoogleIdToken = useCallback(() => {
    const idToken = googleIdTokenRef.current;
    googleIdTokenRef.current = undefined;
    return idToken;
  }, []);

  usePostLoginRedirect({
    onError: setError,
    requiredApplication,
    requiredApplicationName,
    twoStep,
    takeGoogleIdToken,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
    unsavedChangesGuard: false,
  });

  const { mutate } = useMutation({
    ...logCreateMutation(),
  });

  const onSubmit = async (formData: LoginFormData) => {
    try {
      // /*
      //   This is a hotfix for https://idu-identification.atlassian.net/browse/PP-430 and needs a better long term solution for all apps, not just Checkd.
      // */
      localStorage.removeItem('loginMessage');

      setError(null);
      const result = await FirebaseAuthentication.signInWithEmailAndPassword({
        email: formData.email,
        password: formData.password,
      });

      const user = result.user;

      if (!user) return;

      logSuccessfulLogin(user, mutate);
    } catch (err: any) {
      console.error('[LoginPage] Email sign-in failed', err);
      setError(toFirebaseAuthError(err));
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      localStorage.removeItem('loginMessage');
      setIsGoogleSubmitting(true);
      setError(null);

      const result = await FirebaseAuthentication.signInWithGoogle({
        customParameters: [{ key: 'claims', value: GOOGLE_SIGN_IN_STRENGTH_CLAIMS }],
      });
      const user = result.user;
      googleIdTokenRef.current = result.credential?.idToken;

      if (!user) return;

      logSuccessfulLogin(user, mutate);
    } catch (err: any) {
      console.error('[LoginPage] Google sign-in failed', err);
      setError(toFirebaseAuthError(err));
    } finally {
      setIsGoogleSubmitting(false);
    }
  };

  if (ota && isCheckingOta) {
    return <>{ota.loadingFallback}</>;
  }

  if (twoStep.step) {
    return (
      <AuthLayout
        main={
          <LoginColumn>
            <LoginIntro logo={Logo} />
            <TwoStepSteps
              {...twoStep}
              step={twoStep.step}
              onCancel={() => {
                twoStep.reset();
                void logOut();
              }}
            />
          </LoginColumn>
        }
        footer={<AuthLayoutIduFooter showBrand={!hideFooter} showVersion />}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AuthLayout
        main={
          <LoginColumn>
            <LoginIntro logo={Logo} message={instructions ?? loginMessage ?? undefined} />

            <AuthErrorAlert error={error} />

            <OtaInstructionContent
              ota={ota}
              hasInactivityLogout={hasInactivityLogout}
              otaEnforcement={otaEnforcement}
              onAppStoreClick={handleAppStoreClick}
            />
            {!ota && instructionBlock}

            {!(hideLogin || isUpdateMandatory) && (
              <LoginCredentialsSection
                control={control}
                isSubmitting={isSubmitting}
                isDisabled={isGoogleSubmitting}
                emailError={!!errors.email}
                passwordError={!!errors.password}
                showPassword={showPassword}
                onTogglePassword={() => setShowPassword(!showPassword)}
              />
            )}

            {!isUpdateMandatory && (
              <OauthSection
                enableGoogle={enableGoogleLogin}
                isGoogleLoading={isGoogleSubmitting}
                disabled={isSubmitting}
                onGoogleSignIn={handleGoogleSignIn}
              />
            )}

            <SignupPrompt hideSignup={hideSignup} />
          </LoginColumn>
        }
        footer={<AuthLayoutIduFooter showBrand={!hideFooter} showVersion />}
      />
    </form>
  );
};

export default LoginPage;
